import * as React from 'react';
import { useStaticQuery } from 'gatsby';
import AccessiBe from '../AccessiBe';
import AdaChatBot from '../AdaChatBot';
import BingPixel from '../BingPixel';
import FacebookPixel from '../FacebookPixel';
import HotjarPixel from '../HotjarPixel';
import NextdoorPixel from '../NextdoorPixel';
import RedditPixel from '../RedditPixel';
import TiktokPixel from '../TiktokPixel';
import YelpPixel from '../YelpPixel';
import YellowAIChatbot from '../YellowAIChatbot';
import ConvertABScript from '../ConvertABScript';
import { AdaChatBotQuery, adaSettingsQuery } from '../AdaChatBot/AdaChatBot';

type PostBodyComponentProps = {
    isBookingPage: boolean;
    isCMS: boolean;
};

type Decision = 'pending' | 'ada' | 'yellow';

const POLL = 200;
const GRACE_MS = 1500; // time to wait for Convert to set the flag once detected
const MAX_WAIT = 3000; // time to wait for convert until showing just ada

export default function PostBodyComponents({
    isBookingPage,
    isCMS,
}: PostBodyComponentProps) {
    const [decision, setDecision] = React.useState<Decision>('pending');
    const { settings } = useStaticQuery<AdaChatBotQuery>(adaSettingsQuery);
    const adaEnabled = settings?.adaChatBot?.enabled;

    React.useEffect(() => {
        const hasConvert = () => !!(window.convert || window._conv_q);

        let seenConvertAt: number | null = null;
        const start = Date.now();

        const tick = () => {
            // to facilitate the prod deployment, if ada is turned off we show yellow
            // regardless of the experiment
            if (!adaEnabled) {
                setDecision('yellow');
                return;
            }
            const hasFlag = 'enableYellowAIChatbot' in window;
            const flagOn = hasFlag && window.enableYellowAIChatbot;

            if (hasFlag) {
                // flag has been set, no need for timeouts
                setDecision(flagOn ? 'yellow' : 'ada');
                return;
            }

            const convert = hasConvert();

            // convert has loaded but the flag isn't there.
            // wait a bit in case there was a delay setting it up
            if (convert) {
                if (seenConvertAt == null) seenConvertAt = Date.now();
                if (Date.now() - seenConvertAt >= GRACE_MS) setDecision('ada');
                return;
            }

            // fallback: if convert experiment not present, show ada
            if (Date.now() - start >= MAX_WAIT) setDecision('ada');
        };

        const id = window.setInterval(tick, POLL);
        tick();
        return () => window.clearInterval(id);
    }, [adaEnabled]);

    React.useEffect(() => {
        document.documentElement.classList.toggle('hide-bot', isBookingPage);
    }, [isBookingPage]);

    return (
        <>
            <AccessiBe />
            {decision === 'yellow' && <YellowAIChatbot isCMS={isCMS} />}
            {decision === 'ada' && (
                <AdaChatBot isBookingPage={isBookingPage} isCMS={isCMS} />
            )}
            <ConvertABScript />
            <BingPixel />
            <FacebookPixel />
            <HotjarPixel />
            <NextdoorPixel />
            <RedditPixel />
            <TiktokPixel />
            <YelpPixel />
        </>
    );
}
